// Access Assets Application
// Updated: 2024-01-05 - Fixed category detail import
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { SystemOptionsProvider } from '@/contexts/SystemOptionsContext';
import { LanguageSettingsProvider } from '@/contexts/LanguageSettingsContext';
import { PermissionsProvider } from '@/providers/PermissionsProvider';
import { RightPanelProvider } from '@/contexts/RightPanelContext';
import { PageRefreshProvider } from '@/contexts/PageRefreshContext';
import { TourProvider } from '@/contexts/TourContext';
import { AssetDetailSheetProvider } from '@/hooks/use-asset-detail-sheet';
import { ImportAssetsModalProvider } from '@/contexts/ImportAssetsModalContext';
import { ImportAssetsModal } from '@/components/import/import-assets-modal';
import Dashboard from '@/pages/dashboard';
import DashboardNew from '@/pages/dashboard-new';
import AssetsPage from '@/pages/assets';
import AssetDetail from '@/pages/assets/AssetDetail';
import AdditionsPage from '@/pages/transactions/additions';
import DepreciationPage from '@/pages/transactions/depreciation';
import RevaluationPage from '@/pages/transactions/revaluation';
import InsightsPage from '@/pages/insights';
import Enquiry from '@/pages/enquiry';
import HelpPage from '@/pages/help';
import LocationsPage from '@/pages/settings/locations';
import LocationDetail from '@/pages/settings/location-detail';
import CategoriesPage from '@/pages/settings/categories';
import CategoryDetailPage from '@/pages/administration/category-detail';
import EquipmentPage from '@/pages/settings/equipment';
import EquipmentDetailPage from '@/pages/settings/equipment-detail';
import DisposalReasonsPage from '@/pages/settings/disposal-reasons';
import AssetOptionsPage from '@/pages/settings/asset-options';
import FeaturesSettingsPage from '@/pages/settings/features';
import IntegrationLogPage from '@/pages/settings/integration-log';
import PeriodManagementPage from '@/pages/settings/period-management';
import CompliancePage from '@/pages/compliance';
import ComplianceAssetsPage from '@/pages/compliance-assets';
import ComplianceRegulationsPage from '@/pages/compliance-regulations';
import ComplianceSchedulePage from '@/pages/compliance-schedule';
import AuditPage from '@/pages/audit';
import CheckInOutDashboard from '@/pages/checkinout/dashboard';
import CheckInOutCalendar from '@/pages/checkinout/calendar';
import CheckInOutSettingsPage from '@/pages/checkinout/settings';
import CheckOutAsset from '@/pages/checkinout/checkout';
// Asset Import Module
import ImportMethodSelection from '@/pages/assets/import';
import CSVImport from '@/pages/assets/import/csv';
import AccountingSync from '@/pages/assets/import/accounting-sync';
import TransactionScan from '@/pages/assets/import/transaction-scan';

// New Audit Management Module (Redesign)
import ActiveAudits from '@/pages/audits/ActiveAudits';
import ScheduledAudits from '@/pages/audits/ScheduledAudits';
import AuditHistory from '@/pages/audits/AuditHistory';
import { ExceptionsRedesigned as ExceptionsPage } from '@/pages/audits/ExceptionsRedesigned';
import AuditDetail from '@/pages/audits/AuditDetail';
import CreateAudit from '@/pages/audits/CreateAudit';
import ExceptionDetail from '@/pages/ExceptionDetail';
import TasksHome from '@/pages/tasks/TasksHome';
import TaskDetail from '@/pages/tasks/TaskDetail';
import TeamsPermissionsPage from '@/pages/administration/teams-permissions';
import { Toaster } from '@/components/ui/toaster';
import { NavigationTracker } from '@/components/NavigationTracker';

function AppContent() {
  return (
    <SystemOptionsProvider>
      <LanguageSettingsProvider>
        <PermissionsProvider>
          <PageRefreshProvider>
          <TourProvider>
            <AssetDetailSheetProvider>
              <ImportAssetsModalProvider>
                <BrowserRouter>
                  <NavigationTracker />
                  <Routes>
                    <Route path="/" element={<AppLayout />}>
                      <Route index element={<Navigate to="/dashboard" replace />} />
                      <Route path="dashboard" element={<DashboardNew />} />
                      <Route path="dashboard-old" element={<Dashboard />} />
                      <Route path="assets" element={<AssetsPage />} />
                      <Route path="assets/:assetId" element={<AssetDetail />} />
                      <Route path="assets/import" element={<ImportMethodSelection />} />
                      <Route path="assets/import/csv" element={<CSVImport />} />
                      <Route path="assets/import/accounting-sync" element={<AccountingSync />} />
                      <Route path="assets/import/transaction-scan" element={<TransactionScan />} />
                      <Route path="transactions">
                        <Route path="additions" element={<AdditionsPage />} />
                        <Route path="depreciation" element={<DepreciationPage />} />
                        <Route path="revaluation" element={<RevaluationPage />} />
                      </Route>
                      <Route path="insights" element={<InsightsPage />} />
                      <Route path="enquiry" element={<Enquiry />} />
                      <Route path="audit" element={<AuditPage />} />
                      <Route path="compliance" element={<CompliancePage />} />
                      <Route path="compliance/assets" element={<ComplianceAssetsPage />} />
                      <Route path="compliance/regulations" element={<ComplianceRegulationsPage />} />
                      <Route path="compliance/schedule" element={<ComplianceSchedulePage />} />
                      
                      {/* Tasks Module */}
                      <Route path="tasks">
                        <Route index element={<TasksHome />} />
                        <Route path="home" element={<TasksHome />} />
                        <Route path=":taskId" element={<TaskDetail />} />
                      </Route>
                      
                      <Route path="checkinout">
                        <Route index element={<CheckInOutDashboard />} />
                        <Route path="calendar" element={<CheckInOutCalendar />} />
                        <Route path="checkout" element={<CheckOutAsset />} />
                        <Route path="settings" element={<CheckInOutSettingsPage />} />
                      </Route>
                      
                      {/* Audit Management Module Routes */}
                      <Route path="audits">
                        <Route index element={<Navigate to="/audits/active" replace />} />
                        <Route path="dashboard" element={<Navigate to="/audits/active" replace />} />
                        <Route path="active" element={<ActiveAudits />} />
                        <Route path="scheduled" element={<ScheduledAudits />} />
                        <Route path="history" element={<AuditHistory />} />
                        <Route path="exceptions" element={<ExceptionsPage />} />
                        <Route path="exceptions/:id" element={<ExceptionDetail />} />
                        <Route path="create" element={<CreateAudit />} />
                        <Route path=":id" element={<AuditDetail />} />
                      </Route>
                      <Route path="administration">
                        <Route path="teams-permissions" element={<TeamsPermissionsPage />} />
                        <Route path="imports" element={<Navigate to="/assets/import" replace />} />
                        <Route path="locations" element={<LocationsPage />} />
                        <Route path="locations/:locationId" element={<LocationDetail />} />
                        <Route path="categories" element={<CategoriesPage />} />
                        <Route path="categories/:id" element={<CategoryDetailPage />} />
                        <Route path="equipment" element={<EquipmentPage />} />
                        <Route path="equipment/:id" element={<EquipmentDetailPage />} />
                        <Route path="disposal-reasons" element={<DisposalReasonsPage />} />
                      </Route>
                      <Route path="settings">
                        <Route path="features" element={<FeaturesSettingsPage />} />
                        <Route path="asset-options" element={<AssetOptionsPage />} />
                        <Route path="integration-log" element={<IntegrationLogPage />} />
                        <Route path="period-management" element={<PeriodManagementPage />} />
                      </Route>
                      <Route path="help" element={<HelpPage />} />
                    </Route>
                  </Routes>
                  <Toaster />
                  <ImportAssetsModal />
                </BrowserRouter>
              </ImportAssetsModalProvider>
            </AssetDetailSheetProvider>
          </TourProvider>
        </PageRefreshProvider>
        </PermissionsProvider>
      </LanguageSettingsProvider>
    </SystemOptionsProvider>
  );
}

export default function App(): React.ReactElement {
  return (
    <RightPanelProvider>
      <AppContent />
    </RightPanelProvider>
  );
}
